import React, { useState } from "react";
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Box,
  Snackbar,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";

import { useResetPasswordMutation } from "../../Apis/authApi";
let logo2 = require("../../Assets/Images/logoDEC.png");

export default function ChangePassword() {
  const navigate = useNavigate();
  const location = useLocation();

  // Extract token from URL
  const queryParams = new URLSearchParams(location.search);
  const token = queryParams.get("token") || "";

  const [resetPassword] = useResetPasswordMutation();

  const [formData, setFormData] = useState({
    newPassword: "",
    confirmPassword: "",
  });

  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [validated, setValidated] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };
  const [snackbar, setSnackbar] = useState<{
      open: boolean;
      message: string;
      severity: "success" | "error" | "info";
    }>({ open: false, message: "", severity: "info" });

    const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess(false);
    setValidated(true);

    // Validation
    if (formData.newPassword.length < 6) {
      setError("Password must be at least 6 characters long");
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        token: token,
        newPassword: formData.newPassword,
        confirmPassword: formData.confirmPassword,
      };

      await resetPassword(payload).unwrap();
      setSuccess(true);
      setSnackbar({
          open: true,
          message: "Password changed successfully!",
          severity: "success",
        });
      
      setTimeout(() => navigate("/login"), 2000);
    } catch (err: any) {
      if (err.data?.errorMessages) {
        setError(err.data.errorMessages[0]);
        setSnackbar({
          open: true,
          message: error,
          severity: "error",
        });
        
      } else {
        setError("Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container
      maxWidth="lg"
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Grid container justifyContent="center">
        <Grid size={{ xs: 12, md: 12, lg: 12 }}>
          <Card
            elevation={3}
            sx={{
              borderRadius: 3,
              border: "1px solid #ddd",
              p: 1,
            }}
          >
            <Box
              sx={{
                textAlign: "center",
                mb: 2,
                borderBottom: "2px solid rgba(81, 35, 132, 1)",
              }}
            >
              <Box
                component="img"
                src={logo2}
                alt="Logo"
                sx={{
                  maxHeight: 40,
                  display: "inline-block",
                  pb: 0.5, // small space between image and underline
                }}
              />
            </Box>
            <CardContent>
              <Typography
                variant="h6"
                align="left"
                gutterBottom
                sx={{ fontWeight: "bold" }}
              >
                Change Password
              </Typography>

              <Box component="form" onSubmit={handleSubmit} noValidate>
                <TextField
                  label="New Password"
                  type="password"
                  name="newPassword"
                  value={formData.newPassword}
                  onChange={handleChange}
                  fullWidth
                  required
                  variant="outlined"
                  error={validated && formData.newPassword.length < 6}
                  helperText={
                    validated && formData.newPassword.length < 6
                      ? "Password must be at least 6 characters."
                      : ""
                  }
                  sx={{ mb: 3 }}
                />

                <TextField
                  label="Confirm Password"
                  type="password"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  fullWidth
                  required
                  variant="outlined"
                   error={
              validated &&
              (formData.confirmPassword === "" ||
                formData.newPassword !== formData.confirmPassword)
            }
            helperText={
              validated &&
              (formData.confirmPassword === "" ||
                formData.newPassword !== formData.confirmPassword)
                ? "Passwords do not match."
                : ""
            }
                  sx={{ mb: 3 }}
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={loading}
                  sx={{
                    backgroundColor: "#eb3c96",
                    color: "white",
                    "&:hover": { backgroundColor: "#d83485" },
                    py: 1.2,
                  }}
                >
                  {loading ? (
                    <>
                      <CircularProgress
                        size={20}
                        sx={{ color: "white", mr: 1 }}
                      />
                      Changing...
                    </>
                  ) : (
                    "Change Password"
                  )}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}
